import { ds } from "js/core/models/dataService";
import { app } from "js/namespaces.js";
import { Theme } from "js/core/models/theme";
import { THEME_ATTRIBUTES_MAPPING, getThemeFromPresentationModel } from "common/themes";
import { SharedTheme } from "js/core/models/sharedTheme";
import logger from "./logger";
import StorageModel from "js/core/storage/storageModel";

class ThemeManager {
    getTheme(themeId) {
        return new Promise((resolve, reject) => {
            if (!themeId) {
                logger.warn("No user theme provided");
                if (ds.userThemes && ds.userThemes.filter(theme => !theme.get("private")).length > 0) {
                    logger.warn("Falling back to first user theme");
                    resolve(ds.userThemes.filter(theme => !theme.get("private"))[0]);
                } else {
                    logger.warn("Falling back to first built-in theme");
                    resolve(ds.builtInThemes.first());
                }
            }
            let theme;
            if (ds.userThemes) {
                theme = ds.userThemes.get(themeId);

                if (!theme) {
                    logger.warn("Migrating builtInTheme to userTheme");
                    // if this is a pre-Sept 2016 presentation, it's themeId may be a builtInTheme so check the userThemes to find a builtInTheme that matches
                    theme = ds.userThemes.find({ builtInThemeID: themeId });
                    if (theme) {
                        ds.selection.presentation.update({ themeId: theme.id });
                    }
                }
            }

            if (!theme) {
                logger.warn("Falling back on builtInTheme (this should only happen in player)");
                theme = ds.builtInThemes.find({ id: themeId });
            }

            if (theme) {
                resolve(theme);
            } else {
                StorageModel.fetchData(themeId, { root: "user_themes" }).then(data => {
                    resolve(new Theme(data));
                }).catch(() => {
                    logger.warn(`No theme with id ${themeId} was found. Falling back to first userTheme`);
                    resolve(ds.builtInThemes.first());
                });
            }
        });
    }

    loadTheme(presentation) {
        let promise;
        const theme = this.loadThemeFromPresentation(presentation);

        if (!theme.has("colors")) {
            theme.set("colors", theme.getColors());
        }
        if (!theme.has("iconStyle")) {
            theme.set("iconStyle", "chunky");
        }

        if (!presentation.has("theme_colors") && !presentation.has("sharedThemeId")) {
            logger.info("Migrating theme into presentation");

            // copy properties from userTheme
            promise = this.getTheme(presentation.get("themeId")).then(defaultTheme => {
                // migrate old theme
                switch (defaultTheme.get("styleWeight")) {
                    case "thin":
                        defaultTheme.set({
                            styleElementStyle: 0,
                            styleFontWeight: "light"
                        });
                        break;
                    case "medium":
                        defaultTheme.set({
                            styleElementStyle: 1,
                            styleFontWeight: "light"
                        });
                        break;
                    case "heavy":
                        defaultTheme.set({
                            styleElementStyle: 2,
                            styleFontWeight: "heavy"
                        });
                        break;
                }

                //merge theme with any existing presentation properties
                Object.keys(defaultTheme.attributes).forEach(key => {
                    if (!theme.has(key)) {
                        theme.set(key, defaultTheme.get(key));
                    }
                });

                this.saveThemeToPresentation(theme, presentation, { internalChange: true });
                return theme;
            });
        } else {
            promise = Promise.resolve(theme);
        }

        return promise.then(theme => {
            return theme.loadTheme().then(() => {
                app.currentTheme = theme;

                // inject css for text element
                document.documentElement.style.setProperty("--header-font", app.fontManager.fontCache[theme.get("fontHeaderRegular")].name);
                document.documentElement.style.setProperty("--header-font-bold", app.fontManager.fontCache[theme.get("fontHeaderEmphasized")].name);
                document.documentElement.style.setProperty("--header-font-italic", app.fontManager.fontCache[theme.get("fontHeaderItalic")].name);
                document.documentElement.style.setProperty("--header-font-bold-italic", app.fontManager.fontCache[theme.get("fontHeaderEmphasizedItalic")].name);

                document.documentElement.style.setProperty("--title-font", app.fontManager.fontCache[theme.get("fontTitleRegular")].name);
                document.documentElement.style.setProperty("--title-font-bold", app.fontManager.fontCache[theme.get("fontTitleEmphasized")].name);
                document.documentElement.style.setProperty("--title-font-italic", app.fontManager.fontCache[theme.get("fontTitleItalic")].name);
                document.documentElement.style.setProperty("--title-font-bold-italic", app.fontManager.fontCache[theme.get("fontTitleEmphasizedItalic")].name);

                document.documentElement.style.setProperty("--body-font", app.fontManager.fontCache[theme.get("fontBodyRegular")].name);
                document.documentElement.style.setProperty("--body-font-bold", app.fontManager.fontCache[theme.get("fontBodyEmphasized")].name);
                document.documentElement.style.setProperty("--body-font-italic", app.fontManager.fontCache[theme.get("fontBodyItalic")].name);
                document.documentElement.style.setProperty("--body-font-bold-italic", app.fontManager.fontCache[theme.get("fontBodyEmphasizedItalic")].name);

                let style = document.createElement("style");
                style.type = "text/css";
                document.head.appendChild(style);

                let css = "";
                for (let [key, color] of Object.entries(theme.palette.getBackgroundColors(true))) {
                    css += `.auto-emphasized-${key} b {color: ${color}}\n`;
                }
                style.appendChild(document.createTextNode(css));
            });
        });
    }

    saveThemeToPresentation(theme, presentation, options) {
        const timestamp = new Date().getTime();

        const updates = {
            firstSlideModifiedAt: timestamp,
            sharedThemeId: null
        };

        THEME_ATTRIBUTES_MAPPING.forEach(keys => {
            const presentationKey = keys[0];
            const themeKey = keys[1];
            updates[presentationKey] = theme.attributes[themeKey] != undefined ? theme.attributes[themeKey] : null;
        });

        presentation.update(updates, { replaceKeys: true, unsetSilent: true, ...options });
        presentation.slides.forEach(slide => slide.update({ modifiedAt: timestamp }, { confirmChanges: true }));
    }

    saveSharedThemeToPresentation(theme, presentation, options) {
        const timestamp = new Date().getTime();

        const updates = {
            firstSlideModifiedAt: timestamp,
            sharedThemeId: theme.id,
            themeId: null
        };

        THEME_ATTRIBUTES_MAPPING.forEach(keys => {
            const presentationKey = keys[0];
            updates[presentationKey] = null;
        });

        presentation.update(updates, { replaceKeys: true, unsetSilent: true, ...options });
        presentation.slides.forEach(slide => slide.update({ modifiedAt: timestamp }, { confirmChanges: true }));
    }

    loadThemeFromPresentation(presentation) {
        if (presentation.has("sharedThemeId")) {
            return new SharedTheme({ id: presentation.get("sharedThemeId") });
        } else {
            const theme = getThemeFromPresentationModel(presentation.attributes);
            return new Theme(theme, { disconnected: true, autoLoad: false });
        }
    }

    themeChanged(presentation) {
        for (let i = 0; i < THEME_ATTRIBUTES_MAPPING.length; i++) {
            if (presentation.hasChanged(THEME_ATTRIBUTES_MAPPING[i][0])) {
                return true;
            }
        }
        if (presentation.hasChanged("sharedThemeId")) {
            return true;
        }
        return false;
    }
}

app.themeManager = new ThemeManager();

export { ThemeManager };
